// Samordningsnummer, issued to people not registered in Sweden, add 60 to the day.
const COORDINATION_NUMBER_DAY_OFFSET = 60;

/** Checks a Swedish SSN (personnummer / samordningsnummer). */
export function isValidSwedishSsn(ssn: string): boolean {
  // Swedish personnummer / samordningsnummer format: [CC]YYMMDD[-+][Birth Number][Check Digit]
  // "+" marks a person aged 100 or more; the separator is optional.
  const ssnRegex = /^(\d{2})?(\d{2})(\d{2})(\d{2})([-+]?)(\d{3})(\d)$/;
  const match = ssn.match(ssnRegex);

  if (!match) {
    return false;
  }

  const [, century, year, month, day, separator, birthNumber, checkDigit] = match;

  const dayNum = parseInt(day, 10);
  const birthDay = dayNum > COORDINATION_NUMBER_DAY_OFFSET ? dayNum - COORDINATION_NUMBER_DAY_OFFSET : dayNum;
  const fullYear = century
    ? parseInt(century + year, 10)
    : resolveFullYearFromTwoDigitYear(parseInt(year, 10), separator === "+");

  if (!isCalendarDate(birthDay, parseInt(month, 10), fullYear)) {
    return false;
  }

  // Birth numbers run from 001 to 999
  if (birthNumber === "000") {
    return false;
  }

  // The check digit is computed over the 10-digit form, without the century
  return calculateLuhnCheckDigit(year + month + day + birthNumber) === parseInt(checkDigit, 10);
}

/** The 10-digit form means born within the last 100 years, or 100+ years ago with a "+" separator. */
function resolveFullYearFromTwoDigitYear(twoDigitYear: number, isAged100OrMore: boolean): number {
  const currentYear = new Date().getFullYear();
  const currentCenturyYear = currentYear - (currentYear % 100) + twoDigitYear;
  const lastHundredYearsYear = currentCenturyYear > currentYear ? currentCenturyYear - 100 : currentCenturyYear;
  return isAged100OrMore ? lastHundredYearsYear - 100 : lastHundredYearsYear;
}

function isCalendarDate(day: number, month: number, fullYear: number): boolean {
  const date = new Date(fullYear, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day;
}

function calculateLuhnCheckDigit(digits: string): number {
  let sum = 0;
  for (const [i, digit] of [...digits].entries()) {
    const product = parseInt(digit, 10) * (i % 2 === 0 ? 2 : 1);
    sum += product > 9 ? product - 9 : product;
  }
  return (10 - (sum % 10)) % 10;
}
